/**
 * How rich text authored in {@link TextEditor} is rendered — in the editor
 * itself and anywhere the saved body is shown afterwards.
 *
 * Written as explicit utilities rather than `prose`: `@tailwindcss/typography`
 * is a dependency of the app but is never registered as a Tailwind 4 plugin
 * (`@plugin "@tailwindcss/typography"` appears in no stylesheet), so every
 * `prose` class in the codebase currently resolves to nothing. That is why a
 * bulleted list rendered without bullets and a link without an underline. Wiring
 * the plugin up would restyle every `prose` block in the product at once, which
 * is a decision of its own; this styles only what the editor produces.
 *
 * Kept in one constant so the editor and the read-only renderer can't drift —
 * what the admin sees while writing is the whole promise here.
 */
export const RICH_TEXT_CONTENT_CLASS = [
  // Preflight strips list markers and indentation; put them back.
  "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2",
  "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2",
  "[&_li]:my-0.5 [&_li]:pl-1",
  "[&_li>p]:my-0",
  // Paragraph rhythm, without a stray gap at either end of the body.
  "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
  // Pinned rather than inherited, and tight: the gap between two lines joined by
  // a break is this and nothing else, so leaving it to whatever the surrounding
  // page happened to set made a two-line address read as two paragraphs — and
  // made the editor and the saved body disagree about their own spacing.
  "[&_p]:leading-snug [&_li]:leading-snug",
  "[&_strong]:font-semibold",
  "[&_em]:italic",
  "[&_u]:underline",
  // A link has to look like one while it is being written, not only once saved.
  //
  // The product navy rather than `text-brand`: that token is Tailwind's blue-600,
  // which reads as the browser's default link colour and so says nothing about
  // whose product this is. Written as a literal because the palette has no navy
  // token yet — the same value already appears in the app's index.css.
  "[&_a]:text-[#0f2449] [&_a]:underline [&_a]:underline-offset-2",
  // Stated rather than left to the browser: inside the editor the caret cursor
  // wins over the user agent's, so a link there looked like ordinary text.
  "[&_a]:cursor-pointer",
  "[&_a:hover]:opacity-80"
].join(" ");
